import {Indexer, Denormalizer} from 'rlink-cms/indexing';
import formattedImgPath from 'rlink-repos/formattedImgPath';
import {processImages} from 'rlink-cms/indexing/images';
import {mergeStaticOptions} from 'rlink-utils/object';
import ImageItemIndexer from 'rlink-media/indexers/ImageItemIndexer';

export class ProfileIndexer extends ImageItemIndexer {}

ProfileIndexer.contentClassName = 'Profile';

export class HistoryIndexer extends ImageItemIndexer {
  itemData (content) {
    return super.itemData(content)
            .set('iconPath', content.iconPath)
            .set('iconImageFormats', content.iconImageFormats);
  }

  get linkedContentPaths () {
    let pathList = new Set();

    for (let item of this.items) {
      let imagePath = item.get('iconImageFormats');
      for (let format of (imagePath.get('formats') || [{contentType: 'image/jpeg', fileExt: 'jpg'}])) {
        for ( let width of (imagePath.get('widths') || []) ) {
          pathList.add(formattedImgPath(imagePath.get('src'), {width}, format.get('fileExt')));
        }
        for (let height of (imagePath.get('heights') || [])) {
          pathList.add(formattedImgPath(imagePath.get('src'), {height}, format.get('fileExt')));
        }
      }
    }
    return Array.from(pathList).concat(super.linkedContentPaths);
  }
}

HistoryIndexer.contentClassName = 'History';

export class StudioSlideshowImageIndexer extends ImageItemIndexer {}

StudioSlideshowImageIndexer.contentClassName = 'StudioSlideshowImage';

export class CityGalleryImageIndexer extends ImageItemIndexer {}

CityGalleryImageIndexer.contentClassName = 'CityGalleryImage';

export class FranchiseIndexer extends Indexer {
  itemData (content) {
    return content.data;
  }
}

FranchiseIndexer.contentClassName = 'Franchise';


export class ArticlesIndexer extends Indexer {

  itemData (content) {
    const MAX_IMAGE_WIDTH = 680;
    const MAX_IMAGE_HEIGHT = 680;
    let foundMax = false;
    let bannerImageFormats = content.bannerImageFormats;

    if (bannerImageFormats.widths) {
      bannerImageFormats.widths = bannerImageFormats.widths.reverse().reduce((acc, val) => {
        if (!foundMax) {
          if (val >= MAX_IMAGE_WIDTH) {
            foundMax = true;
          }
          return [val, ...acc];
        }
        return acc;
      }, []);
    }

    if (bannerImageFormats.heights) {
      foundMax = false;
      bannerImageFormats.heights = bannerImageFormats.heights.reverse().reduce((acc, val) => {
        if (!foundMax) {
          if (val >= MAX_IMAGE_HEIGHT) {
            foundMax = true;
          }
          return [val, ...acc];
        }
        return acc;
      }, []);
    }

    return content.data
      .delete('body')
      .delete('introduction')
      .delete('authorPath')
      .set('bannerPath', content.bannerPath)
      .set('bannerImageFormats', bannerImageFormats);
  }

  get linkedContentPaths () {
    let pathList = new Set();

    for (let item of this.items) {
      let bannerPath = item.get('bannerImageFormats');
      for (let format of (bannerPath.get('formats') || [{contentType: 'image/jpeg', fileExt: 'jpg'}])) {
        for ( let width of (bannerPath.get('widths') || []) ) {
          pathList.add(formattedImgPath(bannerPath.get('src'), {width}, format.get('fileExt')));
        }
        for (let height of (bannerPath.get('heights') || [])) {
          pathList.add(formattedImgPath(bannerPath.get('src'), {height}, format.get('fileExt')));
        }
      }

      if (typeof (item.get('body')) !== 'undefined') {
        let matchAll = item.get('body').match(/.*?src="(\/content\/.*?)"/g);
        if (matchAll) {
          for (let i = 0; i < matchAll.length; i++) {
            let match = matchAll[i].match(/.*?src="(\/content\/.*?)"/);
            pathList.add(match[1]);
          }
        }
      }
    }

    return Array.from(pathList);
  }

  get sortKey () {
    return ['publishedOn'];
  }
}


ArticlesIndexer.contentClassName = 'RelicArticle';

ArticlesIndexer.translatedAttributes = {
  title: {
    path: ['items'],
    values: ['title'],
    translationSource: {
      contentPath: ['path'],
      translationAttr: 'title',
    },
  },
};

export class ArticleBySlug extends Denormalizer {
  get linkedContentPaths () {
    let pathList = new Set();
    let bannerPath = this.data.get('bannerImageFormats');
    for (let format of (bannerPath.get('formats') || [{contentType: 'image/jpeg', fileExt: 'jpg'}])) {
      for ( let width of (bannerPath.get('widths') || []) ) {
        pathList.add(formattedImgPath(bannerPath.get('src'), {width}, format.get('fileExt')));
      }
      for (let height of (bannerPath.get('heights') || [])) {
        pathList.add(formattedImgPath(bannerPath.get('src'), {height}, format.get('fileExt')));
      }
    }

    if (typeof (this.data.get('body')) !== 'undefined') {
      let matchAll = this.data.get('body').match(/.*?src="(\/content\/.*?)"/g);
      if (matchAll) {
        for (let i = 0; i < matchAll.length; i++) {
          let match = matchAll[i].match(/.*?src="(\/content\/.*?)"/);
          pathList.add(match[1]);
        }
      }
    }

    return Array.from(pathList);
  }

  static contentData (content) {
    let bannerImageFormats = (content.bannerImageFormats) ? content.bannerImageFormats : {}; // we get authors in here too
    return content.data.set('bannerPath', content.bannerPath).set('$path', content.path).set('bannerImageFormats', bannerImageFormats);
  }

  static shouldDenormalize (mainContent) {
    return mainContent && mainContent.data && !mainContent.data.get('isExternal');
  }
}

mergeStaticOptions(ArticleBySlug, {
  mainContentClass: 'RelicArticle',
  mapping: {
    Author: ['authorPath', 'author'],
  },
  pathKey: 'slug',
});

ArticleBySlug.translatedAttributes = {
  title: {
    path: ['title'],
    translationSource: {
      contentPath: ['$path'],
      translationAttr: 'title',
    },
  },
  body: {
    path: ['body'],
    translationSource: {
      contentPath: ['$path'],
      translationAttr: 'body',
    },
  },
};

export default async function (command, repo) {
  async function getAllArticles () {
    return Promise.all(await repo.mapPaths('/content/relicarticle/',
                       path => repo.fetchContentAtPath(path)));
  }

  async function getAllHistory () {
    return Promise.all(await repo.mapPaths('/content/history/',
                       path => repo.fetchContentAtPath(path)));
  }

  async function getAllProfile () {
    return Promise.all(await repo.mapPaths('/content/profile/',
                       path => repo.fetchContentAtPath(path)));
  }

  async function getAllStudioSlides () {
    return Promise.all(await repo.mapPaths('/content/studioslideshowimage/',
                       path => repo.fetchContentAtPath(path)));
  }

  async function getAllCityGalleryImages () {
    return Promise.all(await repo.mapPaths('/content/citygalleryimage/',
                       path => repo.fetchContentAtPath(path)));
  }

  async function getAllProcessImageInstances () {
    return [
      ...await getAllArticles(),
      ...await getAllHistory(),
      ...await getAllProfile(),
      ...await getAllStudioSlides(),
      ...await getAllCityGalleryImages(),
    ];
  }

  await repo.submit(
    await ArticlesIndexer.index(command, repo),
    ...await ArticleBySlug.index(command, repo),
    ...await processImages(command, repo, getAllProcessImageInstances),
    await FranchiseIndexer.index(command, repo),
    await StudioSlideshowImageIndexer.index(command, repo),
    await HistoryIndexer.index(command, repo),
    await ProfileIndexer.index(command, repo),
    await CityGalleryImageIndexer.index(command, repo),
  );
}



// WEBPACK FOOTER //
// ./apps/www.relic.com/src/indexers.js